import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { StyledProps } from 'styled-components'
import { dynamicInputCss, focusInputCss, InputStyled as Input } from '../Input/styles'
import { FormField } from '../../form'

type Placement = 'left' | 'right'
type InputSize = 'thin' | 'small' | 'medium' | 'big'

export type PrefixInputProps = {
  value: string | number
  prefix: string | React.ReactNode
  prefixPlacement?: Placement
  placeholder?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  invalid?: boolean
  errorText?: string
  label?: string
  name?: string
  id?: string
  disabled?: boolean
  onFocus?(e: React.FocusEvent<HTMLInputElement>): void
  onBlur?(e: React.FocusEvent<HTMLInputElement>): void
  className?: string
  type?: string
  inputSize?: InputSize
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'>

const InputsContainer = styled.div<
  StyledProps<{ disabled?: boolean; invalid?: boolean; focused: boolean; inputSize: InputSize }>
>`
  display: flex;
  flex-direction: row;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  align-items: stretch;
  width: 100%;
  background: ${({ theme }) => theme.color.onPrimaryLight};
  ${dynamicInputCss}
  ${props => props.focused && focusInputCss(props)};
`

const inputCss = ({ inputSize }: { inputSize: InputSize }) => {
  let height
  switch (inputSize) {
    case 'thin':
      height = '26px'
      break
    case 'small':
      height = '30px'
      break
    case 'medium':
      height = '38px'
      break
    case 'big':
      height = '46px'
      break
    default:
      height = '38px'
  }
  return {
    height,
  }
}

const InputStyled = styled(Input)`
  border: none;
  ${inputCss};
`

const PrefixContainer = styled.div<{ placement: Placement }>`
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: ${props => (props.placement === 'left' ? '0 0 0 8px' : '0 8px 0 0')};
  order: ${props => (props.placement === 'left' ? 0 : 1)};
  white-space: nowrap;
  font-weight: 500;
  line-height: 20px;
  color: ${props => props.theme.color.onSurfaceMidEmphasys};
`

export const PrefixInput: React.FC<PrefixInputProps> = ({
  value = '',
  prefix,
  prefixPlacement = 'left',
  onChange,
  invalid,
  label,
  id,
  name,
  disabled = false,
  onFocus,
  onBlur,
  errorText,
  placeholder,
  className,
  type,
  inputSize = 'medium',
  ...rest
}) => {
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleFocus: React.FocusEventHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true)
      onFocus && onFocus(e)
    },
    [onFocus],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false)
      onBlur && onBlur(e)
    },
    [onBlur],
  )

  const handlePrefixClick = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  // Prevent cursor jumping to the end of line
  const [val, setVal] = useState(value)

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setVal(event.target.value)
      onChange?.(event, event.target.value)
    },
    [onChange],
  )

  useEffect(() => {
    if (value !== val) setVal(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <FormField
      id={id}
      name={name}
      invalid={invalid}
      errorText={errorText}
      className={className}
      label={label}
      render={({ id, name }) => (
        <InputsContainer focused={focused} invalid={invalid} inputSize={inputSize}>
          <PrefixContainer placement={prefixPlacement} onClick={handlePrefixClick}>
            {prefix}
          </PrefixContainer>
          <InputStyled
            ref={inputRef}
            type={type}
            id={id}
            name={name}
            value={val || ''}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            inputSize={inputSize}
            {...rest}
          />
        </InputsContainer>
      )}
    />
  )
}
